import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Calendar } from 'lucide-react'
import type { ProviderInvoiceDocument } from '@/types'

export interface EngagementListItem {
  id: string
  title: string
  scheduledDate: string | null
  status: string
  clientName: string
}

interface Props {
  assignments: EngagementListItem[]
  myInvoices: Array<ProviderInvoiceDocument & { id: string }>
  hasTaxIdOnFile: boolean
  onSubmitInvoice: (engagement: { id: string; title: string; clientName: string }) => void
}

function fmtScheduledDate(ymd: string | null): string {
  if (!ymd) return 'No date set'
  const [Y, M, D] = ymd.split('-').map(Number)
  if (!Y || !M || !D) return ymd
  return new Date(Y, M - 1, D).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

type InvoiceStatus = ProviderInvoiceDocument['status']

function statusBadge(status: InvoiceStatus | null) {
  if (status === null) {
    return <Badge variant="outline">No invoice yet</Badge>
  }
  switch (status) {
    case 'submitted':
      return <Badge variant="outline">Submitted</Badge>
    case 'approved':
      return <Badge>Approved</Badge>
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>
    case 'paid':
      return <Badge variant="secondary">Paid</Badge>
    case 'draft':
      return <Badge variant="outline">Draft</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

export function EngagementInvoiceList({ assignments, myInvoices, hasTaxIdOnFile, onSubmitInvoice }: Props) {
  const today = new Date()
  const todayYmd = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-')
  const readyToInvoice = assignments
    .filter(
      (assignment) =>
        assignment.status !== 'cancelled' &&
        (assignment.status === 'delivered' ||
          (!!assignment.scheduledDate && assignment.scheduledDate <= todayYmd)),
    )
    .sort((a, b) => (b.scheduledDate ?? '').localeCompare(a.scheduledDate ?? ''))

  if (readyToInvoice.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        No completed or past-date engagements are ready to invoice yet.
      </p>
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-2">
        {readyToInvoice.map((a) => {
          const invoicesForEng = myInvoices.filter((inv) => inv.engagementId === a.id)
          const latest = invoicesForEng[0] ?? null
          const hasExistingInvoice = latest !== null

          let disabled = false
          let tooltip = ''
          if (!hasTaxIdOnFile) {
            disabled = true
            tooltip = 'Complete your W-9 first'
          } else if (hasExistingInvoice) {
            disabled = true
            tooltip = latest.status === 'rejected'
              ? 'Edit and resubmit the rejected invoice from My Invoices'
              : 'You already have an invoice on file for this engagement'
          }

          const button = (
            <Button
              size="sm"
              disabled={disabled}
              onClick={() =>
                onSubmitInvoice({ id: a.id, title: a.title, clientName: a.clientName })
              }
            >
              Submit Invoice
            </Button>
          )

          return (
            <div
              key={a.id}
              className="rounded-md border p-4 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center"
            >
              <div className="min-w-0">
                <p className="font-medium truncate">{a.title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {fmtScheduledDate(a.scheduledDate)}
                  </span>
                  <span>· Client: {a.clientName}</span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                {statusBadge(latest?.status ?? null)}
                {disabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>{button}</span>
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                  </Tooltip>
                ) : (
                  button
                )}
              </div>
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
